import { useEffect, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Scissors, Loader2, X, Play, Flag, Upload } from "lucide-react";

const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round(s || 0) % 60).padStart(2, "0")}`;
const safeName = (s) => (s || "forecast").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "forecast";

/** In-browser clip trimmer: set start/end, re-record the selected range and save it as a NEW clip. */
export function ClipTrimmer({ clip, onClose, onSaved }) {
  const videoRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const [srcUrl, setSrcUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [dur, setDur] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [cur, setCur] = useState(0);
  const [title, setTitle] = useState(`${clip.title || "Forecast"} (trim)`);
  const [recording, setRecording] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [saving, setSaving] = useState(false);

  // Fetch the clip as a same-origin blob so captureStream() isn't blocked by cross-origin tainting.
  useEffect(() => {
    let url;
    (async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}${clip.url}`);
        if (!resp.ok) throw new Error("fetch failed");
        const blob = await resp.blob();
        url = URL.createObjectURL(blob);
        setSrcUrl(url);
      } catch { setLoadErr(true); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [clip.url]);

  const onMeta = () => {
    const raw = videoRef.current?.duration;
    const d = raw && isFinite(raw) && raw > 0 ? raw : (clip.duration_s || 0);
    setDur(d); setStart(0); setEnd(d);
  };
  const onTime = () => setCur(videoRef.current?.currentTime || 0);

  const clampStart = (v) => setStart(Math.max(0, Math.min(v, end - 0.5)));
  const clampEnd = (v) => setEnd(Math.min(dur, Math.max(v, start + 0.5)));
  const seek = (t) => { if (videoRef.current) { videoRef.current.currentTime = t; setCur(t); } };

  const previewRange = async () => {
    const v = videoRef.current; if (!v) return;
    v.muted = false; v.currentTime = start;
    await v.play().catch(() => {});
    const stop = () => { if (v.currentTime >= end) { v.pause(); v.removeEventListener("timeupdate", stop); } };
    v.addEventListener("timeupdate", stop);
  };

  const finishRec = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    const v = videoRef.current;
    if (v) v.pause();
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  }, []);

  const saveTrim = async () => {
    const v = videoRef.current;
    if (!v || end - start < 0.5) { toast.error("Pick a range of at least half a second."); return; }
    let stream;
    try { stream = v.captureStream ? v.captureStream() : v.mozCaptureStream(); }
    catch { toast.error("This browser can't trim video here — use Chrome."); return; }
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2500000 });
    recRef.current = rec; chunksRef.current = [];
    rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    rec.onstop = async () => {
      setRecording(false);
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setSaving(true);
      try {
        const fd = new FormData();
        fd.append("file", blob, `${safeName(title)}.webm`);
        fd.append("title", (title || "Forecast").trim());
        fd.append("duration_s", String(Math.round((end - start) * 10) / 10));
        fd.append("expires_hours", "0");
        await api.post("/forecast-videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Trimmed clip saved — it's in the 24/7 loop.");
        onSaved?.();
      } catch { toast.error("Upload failed — try a shorter trim."); }
      finally { setSaving(false); }
    };
    v.muted = true;
    v.currentTime = start;
    await v.play().catch(() => {});
    setRecording(true); setRecElapsed(0);
    rec.start(500);
    const t0 = performance.now();
    const loop = () => {
      setRecElapsed((performance.now() - t0) / 1000);
      if (videoRef.current && videoRef.current.currentTime >= end - 0.03) { finishRec(); return; }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  useEffect(() => () => { cancelAnimationFrame(rafRef.current); try { recRef.current?.stop?.(); } catch { /* noop */ } }, []);

  const busy = recording || saving;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4" data-testid="clip-trimmer">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-white/15 bg-[#0b1120] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="flex items-center gap-2 font-oswald text-lg font-bold uppercase tracking-wide text-white"><Scissors className="h-5 w-5 text-[#ffd60a]" /> Re-trim · {clip.title}</div>
          <button onClick={onClose} disabled={busy} className="rounded p-1.5 text-gray-400 hover:bg-white/10 hover:text-white disabled:opacity-40" data-testid="clip-trimmer-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">
          {loadErr ? (
            <div className="py-10 text-center font-oswald text-sm uppercase tracking-wider text-[#ffb3bd]">Couldn't load this clip. Try again.</div>
          ) : !srcUrl ? (
            <div className="flex items-center justify-center gap-3 py-10 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> <span className="font-oswald text-sm uppercase tracking-wider">Loading clip…</span></div>
          ) : (
            <>
              <video ref={videoRef} src={srcUrl} controls playsInline onLoadedMetadata={onMeta} onTimeUpdate={onTime} className="w-full rounded-md bg-black" style={{ maxHeight: "46vh" }} data-testid="clip-trimmer-video" />
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between font-oswald text-sm font-bold uppercase tracking-wider text-white">
                  <span>Trim range</span>
                  <span className="text-[#ffd60a]" data-testid="clip-trimmer-range">{mmss(start)} → {mmss(end)} · {mmss(end - start)}</span>
                </div>
                {/* Start handle */}
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-400">
                    <span>Start · {mmss(start)}</span>
                    <button onClick={() => clampStart(cur)} disabled={busy} className="flex items-center gap-1 rounded bg-white/10 px-2 py-1 font-oswald text-[11px] uppercase tracking-wider text-white hover:bg-white/20 disabled:opacity-40" data-testid="clip-trimmer-mark-in"><Flag className="h-3 w-3" /> Set start to playhead</button>
                  </div>
                  <input type="range" min={0} max={dur || 0} step="0.1" value={start} onChange={(e) => { clampStart(Number(e.target.value)); seek(Number(e.target.value)); }} disabled={busy} className="w-full accent-[#1f63c9]" data-testid="clip-trimmer-start" />
                </div>
                {/* End handle */}
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-400">
                    <span>End · {mmss(end)}</span>
                    <button onClick={() => clampEnd(cur)} disabled={busy} className="flex items-center gap-1 rounded bg-white/10 px-2 py-1 font-oswald text-[11px] uppercase tracking-wider text-white hover:bg-white/20 disabled:opacity-40" data-testid="clip-trimmer-mark-out"><Flag className="h-3 w-3" /> Set end to playhead</button>
                  </div>
                  <input type="range" min={0} max={dur || 0} step="0.1" value={end} onChange={(e) => { clampEnd(Number(e.target.value)); seek(Number(e.target.value)); }} disabled={busy} className="w-full accent-[#1f63c9]" data-testid="clip-trimmer-end" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} placeholder="New clip title" data-testid="clip-trimmer-title" className="w-64 rounded-md border border-white/15 bg-black/40 px-3 py-2 font-oswald text-sm text-white placeholder:text-gray-500" />
                  <button onClick={previewRange} disabled={busy} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 disabled:opacity-40" data-testid="clip-trimmer-preview"><Play className="h-4 w-4" /> Preview range</button>
                  <button onClick={saveTrim} disabled={busy || end - start < 0.5} className="flex items-center gap-2 rounded-md bg-[#1f63c9] px-5 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-[#2a74d8] disabled:opacity-50" data-testid="clip-trimmer-save">
                    {recording ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording {mmss(recElapsed)} / {mmss(end - start)}</> : saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Upload className="h-4 w-4" /> Save trim to loop</>}
                  </button>
                  {recording && <span className="font-oswald text-[11px] uppercase tracking-wider text-[#ffd60a]">Re-recording the selected range — plays through once.</span>}
                </div>
                <p className="font-oswald text-[11px] uppercase tracking-wider text-gray-500">The original stays in your library — the trim is saved as a new clip.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
